import type {
  GuestProjectsResponse,
  ProjectMember,
  ProjectShareRequest,
  TeamAssignableRole,
  TeamRoleRequest,
  TeamRoleResponse,
} from '@brand24-original/llm-types';
import { client } from '../apiClient';
import { TeamResponse, UserTeamData } from '../../models/team/TeamResponse';

export const getTeam = async (): Promise<TeamResponse[]> => {
  return client(true, true)
    .get<TeamResponse[]>('/team/data')
    .then((response) => response.data)
    .catch((error) => error);
};

export const removeUser = async (userId: number): Promise<boolean> => {
  return client(true, true)
    .delete<TeamResponse[]>(`/team/data/${userId}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const inviteUser = async (userTeamData: UserTeamData): Promise<boolean> => {
  return client(true, true)
    .post<TeamResponse[]>(`/team/data/add`, userTeamData)
    .then((response) => response.data)
    .catch((error) => error);
};

/** Id projektów, które widzi już zaproszony gość — wypełnia okno edycji zakresu. */
export const getGuestProjects = async (userId: number): Promise<number[]> => {
  return client(true, true)
    .get<{ projectIds: number[] }>(`/team/data/${userId}/projects`)
    .then((response) => response.data.projectIds)
    .catch(() => []);
};

/**
 * Podmienia CAŁY zakres gościa — lista bez projektu znaczy „odbierz mu ten projekt". Bez `canWrite`:
 * the backend keeps each grant's current value (the panel edits WHICH projects, not what a guest may
 * do with them), so an edit cannot silently take a write grant away.
 */
export const updateGuestProjects = async (userId: number, projectIds: number[]): Promise<boolean> => {
  return client(true, true)
    .put<boolean>(`/team/data/${userId}/projects`, {
      projects: projectIds.map((projectId) => ({ projectId })),
    })
    .then((response) => response.data === true)
    .catch(() => false);
};

/** Who sees the project — every member of the account with `assigned` (the hub's "Share project"). */
export const getProjectMembers = async (projectId: number): Promise<ProjectMember[]> => {
  const { data } = await client(true, true).get<ProjectMember[]>(`/projects/${projectId}/members`);
  return data;
};

/**
 * Shares the project with exactly these guests (`PUT /projects/:id/share`) and answers the fresh member
 * list. Throws on failure — a 400 carries `{ error: 'NOT_A_GUEST' | 'GUEST_NEEDS_PROJECT', userId }`.
 */
export const shareProject = async (projectId: number, userIds: number[]): Promise<ProjectMember[]> => {
  const body: ProjectShareRequest = { userIds };
  const { data } = await client(true, true).put<ProjectMember[]>(`/projects/${projectId}/share`, body);
  return data;
};

/**
 * Takes ONE project away from a guest (`DELETE /team/data/:id/projects/:projectId`) and answers the
 * guest's remaining project ids. Throws on failure — the guest's only project is a 400
 * `GUEST_NEEDS_PROJECT`.
 */
export const revokeGuestProject = async (userId: number, projectId: number): Promise<number[]> => {
  const { data } = await client(true, true).delete<GuestProjectsResponse>(
    `/team/data/${userId}/projects/${projectId}`
  );
  return data.projectIds;
};

/**
 * Changes a member's role (`PATCH /team/data/:id/role`); a guest-to-be needs `projectIds`. Throws on
 * failure (400 for the owner, oneself, a guest without a project; 403 without the guest addon).
 */
export const changeMemberRole = async (
  userId: number,
  role: TeamAssignableRole,
  projectIds?: number[]
): Promise<TeamRoleResponse> => {
  const body: TeamRoleRequest = { role, projectIds };
  const { data } = await client(true, true).patch<TeamRoleResponse>(`/team/data/${userId}/role`, body);
  return data;
};
